import { toast } from 'react-toastify';
import { DangerButton } from '../ui/buttons/DangerButton';
import { SecondaryButton } from '../ui/buttons/SecondaryButton';
import Modal from '../layout/Modal';
import DeleteSubmission from '../../api/submissions/DeleteSubmission';
import { useQueryClient } from '@tanstack/react-query';
import renderToastError from '../../utils/renderToastError';
import { FormGroup } from '../form';
import { FormInputLabel } from '../form';
import { TextInput } from '../shared/input/Input';
import { useId, useState, type SubmitEvent } from 'react';
import useSession from '../../hooks/useSession';

interface Props {
    levelID: number;
    userID: number;
    submissionID: number;
    username: string;
    onClose: () => void;
}

export default function DeleteSubmissionModal({ userID, levelID, submissionID, username, onClose: close }: Props) {
    const [deleteReason, setDeleteReason] = useState('');
    const deleteInputID = useId();
    const queryClient = useQueryClient();
    const session = useSession();
    const isOwnSubmission = session.user?.ID === userID;

    const deleteSubmission = (e: SubmitEvent) => {
        e.preventDefault();

        void toast.promise(
            DeleteSubmission(submissionID, deleteReason || undefined).then(() => {
                void queryClient.invalidateQueries({ queryKey: ['level', levelID] });
                void queryClient.invalidateQueries({ queryKey: ['user', userID, 'submissions'] });
                close();
            }),
            {
                pending: 'Deleting...',
                success: 'Submission deleted ',
                error: renderToastError,
            },
        );
    };

    return (
        <Modal title='Delete submission' show={true} onClose={close}>
            <p>
                {isOwnSubmission ? (
                    <>Are you sure you want to delete your submission?</>
                ) : (
                    <>
                        Are you sure you want to delete <b>{username}</b>'s submission?
                    </>
                )}
            </p>
            <form onSubmit={deleteSubmission}>
                {!isOwnSubmission && (
                    <FormGroup>
                        <FormInputLabel htmlFor={deleteInputID}>Reason</FormInputLabel>
                        <TextInput
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                            id={deleteInputID}
                            required
                            invalid={deleteReason.length < 3}
                            minLength={3}
                        />
                    </FormGroup>
                )}
                <div className='flex place-content-end gap-2 mt-4'>
                    <SecondaryButton type='button' onClick={close}>
                        Close
                    </SecondaryButton>
                    <DangerButton type='submit'>Delete</DangerButton>
                </div>
            </form>
        </Modal>
    );
}
